// CachedGridThumbnail.js - Windows File Explorer-style thumbnails
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRuntimeConfig } from '../config';
import { evictThumbCache } from './safeStorage';

// ─── Global request management ───────────────────────────────────────────────
const globalThumbnailCache = new Map();
const activeRequests = new Map();

const MAX_CONCURRENT_FETCHES = 6;
let activeFetches = 0;
const fetchWaiters = [];

const acquireSlot = async () => {
  if (activeFetches < MAX_CONCURRENT_FETCHES) { activeFetches++; return; }
  await new Promise(resolve => fetchWaiters.push(resolve));
  activeFetches++;
};

const releaseSlot = () => {
  activeFetches = Math.max(0, activeFetches - 1);
  const next = fetchWaiters.shift();
  if (next) next();
};

// ─── Shimmer animation (injected once into <head>) ────────────────────────────
let shimmerInjected = false;
const injectShimmer = () => {
  if (shimmerInjected || typeof document === 'undefined') return;
  shimmerInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes _thumb_shimmer {
      0%   { background-position: -200% 0; }
      100% { background-position:  200% 0; }
    }
    ._thumb_shimmer {
      background: linear-gradient(90deg, #ececec 25%, #f8f8f8 50%, #ececec 75%);
      background-size: 200% 100%;
      animation: _thumb_shimmer 1.5s infinite linear;
    }
    .thumb-tile { display: block; }
    .thumb-tile:hover ._thumb_play_badge { opacity: 1; }
  `;
  document.head.appendChild(style);
};

// ─── SVG file-type icons ──────────────────────────────────────────────────────
const ImageFileIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="20" height="20" rx="3" fill="#4caf50" fillOpacity="0.12" stroke="#4caf50" strokeWidth="1.5"/>
    <circle cx="8.5" cy="8.5" r="1.8" fill="#4caf50"/>
    <path d="M2 16 L8 10 L13 15 L17 11 L22 16" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
  </svg>
);

const VideoFileIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="4" width="16" height="16" rx="2.5" fill="#f44336" fillOpacity="0.12" stroke="#f44336" strokeWidth="1.5"/>
    <path d="M17 9 L23 6 L23 18 L17 15 Z" fill="#f44336" stroke="#f44336" strokeWidth="1" strokeLinejoin="round"/>
    <circle cx="8" cy="12" r="3" fill="#f44336" fillOpacity="0.35"/>
    <path d="M6.5 12 L9.5 10.5 L9.5 13.5 Z" fill="#f44336"/>
  </svg>
);

const PdfFileIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
      fill="#e53935" fillOpacity="0.12" stroke="#e53935" strokeWidth="1.5"/>
    <path d="M14 2 L14 8 L20 8" stroke="#e53935" strokeWidth="1.5" fill="none"/>
    <text x="6" y="17.5" fontSize="5.5" fontWeight="800" fill="#e53935"
      fontFamily="Arial,sans-serif" letterSpacing="0.3">PDF</text>
  </svg>
);

const FileIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
      fill="#90a4ae" fillOpacity="0.15" stroke="#90a4ae" strokeWidth="1.5"/>
    <path d="M13 2 L13 9 L20 9" stroke="#90a4ae" strokeWidth="1.5" fill="none"/>
  </svg>
);

// ─── Overlay badges ───────────────────────────────────────────────────────────
const PlayBadge = () => (
  <div style={{
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  }}>
    <div style={{
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.52)',
      backdropFilter: 'blur(2px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
    }}>
      <svg width="11" height="13" viewBox="0 0 11 13" fill="white">
        <polygon points="1,1 10,6.5 1,12"/>
      </svg>
    </div>
  </div>
);

const PdfBadge = () => (
  <div style={{
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    background: 'rgba(213,0,0,0.82)',
    color: 'white',
    fontSize: '9px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    padding: '1px 5px',
    borderRadius: '3px',
    pointerEvents: 'none',
    lineHeight: '15px',
    fontFamily: 'Arial, sans-serif',
  }}>
    PDF
  </div>
);

// ─── Size map ─────────────────────────────────────────────────────────────────
const SIZE_CONFIG = {
  list:   { width: 36,  height: 27  }, // compact for list-view rows
  small:  { width: 80,  height: 60  },
  medium: { width: 120, height: 90  },
  large:  { width: 160, height: 120 },
};

// ─── Main component ───────────────────────────────────────────────────────────
const CachedGridThumbnail = ({
  itemPath,
  isVideo,
  isPdf = false,
  deviceName,
  size = 'medium',
  lazyLoad = true,
  isFileManagerContext = false,
  isEmbedded = false,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(false);
  const [isVisible, setIsVisible]       = useState(!lazyLoad);
  const imgRef     = useRef(null);
  const observerRef = useRef(null);

  const { API_BASE_URL } = getRuntimeConfig();
  const { width, height } = SIZE_CONFIG[size] || SIZE_CONFIG.medium;

  useEffect(() => { injectShimmer(); }, []);

  const cacheKey = isFileManagerContext
    ? `fm_${itemPath}_${size}`
    : `${deviceName}_${itemPath}_${size}`;

  // ── Intersection Observer for lazy loading ──
  const observerCallback = useCallback((entries) => {
    if (entries[0].isIntersecting) {
      setIsVisible(true);
      observerRef.current?.disconnect();
    }
  }, []);

  useEffect(() => {
    if (lazyLoad && imgRef.current && !isVisible) {
      observerRef.current = new IntersectionObserver(observerCallback, {
        threshold: 0.1,
        rootMargin: '120px',
      });
      observerRef.current.observe(imgRef.current);
    }
    return () => observerRef.current?.disconnect();
  }, [lazyLoad, isVisible, observerCallback]);

  // ── localStorage cache helpers ──
  const getCachedThumbnail = useCallback(() => {
    try {
      const raw = localStorage.getItem(`thumb_${cacheKey}`);
      if (raw) {
        const { dataUrl, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < 60 * 60 * 1000) return dataUrl;
        localStorage.removeItem(`thumb_${cacheKey}`);
      }
    } catch (_) {}
    return null;
  }, [cacheKey]);

  const setCachedThumbnail = useCallback((dataUrl) => {
    if (!dataUrl || dataUrl.length >= 150000) return;
    const payload = JSON.stringify({ dataUrl, timestamp: Date.now() });
    try {
      localStorage.setItem(`thumb_${cacheKey}`, payload);
    } catch (_) {
      // Cache full -> evict the oldest half of the (regenerable) thumbnail cache so it
      // can never grow to starve critical writes (the auth token), then retry once.
      try { evictThumbCache(0.5); localStorage.setItem(`thumb_${cacheKey}`, payload); } catch (__) {}
    }
  }, [cacheKey]);

  // ── Thumbnail fetch ──
  const loadThumbnail = useCallback(async () => {
    if (!itemPath || !isVisible) return;

    // 1. In-memory cache
    const cached = globalThumbnailCache.get(cacheKey);
    if (cached) { setThumbnailUrl(cached); setLoading(false); return; }

    // 2. localStorage cache
    const stored = getCachedThumbnail();
    if (stored) {
      globalThumbnailCache.set(cacheKey, stored);
      setThumbnailUrl(stored);
      setLoading(false);
      return;
    }

    // 3. Deduplicate in-flight requests
    if (activeRequests.has(cacheKey)) {
      try {
        const result = await activeRequests.get(cacheKey);
        setThumbnailUrl(result);
        setLoading(false);
      } catch (_) {
        setError(true);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(false);

    const loadPromise = (async () => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      let apiUrl;
      if (isFileManagerContext) {
        const parts = itemPath.split('/').filter(Boolean);
        if (parts.length < 3 || parts[0] !== 'home') throw new Error('Invalid path');
        if (isEmbedded && deviceName) {
          const filename = parts[parts.length - 1];
          apiUrl = `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(deviceName)}/${encodeURIComponent(filename)}`;
          if (isPdf) apiUrl += '?pdf=true';
        } else {
          const username     = parts[1];
          const relativePath = parts.slice(2).join('/');
          apiUrl = `${API_BASE_URL}/api/thumbnail/filemanager/${encodeURIComponent(username)}/${encodeURIComponent(relativePath)}`;
          if (isPdf) apiUrl += '?pdf=true';
        }
      } else {
        if (!deviceName) throw new Error('Device name required');
        apiUrl = `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(deviceName)}/${encodeURIComponent(itemPath)}`;
      }

      await acquireSlot();
      let response;
      try {
        response = await fetch(apiUrl, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'image/jpeg,image/png,image/*' },
          credentials: 'omit',
          signal: AbortSignal.timeout(35000),
        });
      } finally {
        releaseSlot();
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          setCachedThumbnail(dataUrl);
          globalThumbnailCache.set(cacheKey, dataUrl);
          resolve(dataUrl);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    })();

    activeRequests.set(cacheKey, loadPromise);
    try {
      const result = await loadPromise;
      setThumbnailUrl(result);
      setLoading(false);
    } catch (_) {
      setError(true);
      setLoading(false);
    } finally {
      activeRequests.delete(cacheKey);
    }
  }, [itemPath, deviceName, isVisible, cacheKey, getCachedThumbnail, setCachedThumbnail,
      isFileManagerContext, isEmbedded, isPdf, API_BASE_URL]);

  useEffect(() => {
    if (isVisible) loadThumbnail();
  }, [isVisible, loadThumbnail]);

  // ── Shared container style ──
  const tileStyle = {
    position: 'relative',
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: '5px',
    overflow: 'hidden',
    flexShrink: 0,
    background: '#f0f0f0',   // neutral fill for letterbox/pillarbox gaps
  };

  // ── Loading: shimmer skeleton ──
  if (loading) {
    return (
      <div
        ref={imgRef}
        className="_thumb_shimmer"
        style={{ ...tileStyle, border: '1px solid #e2e2e2' }}
      />
    );
  }

  // ── Error: centered file-type icon, click to retry ──
  if (error || !thumbnailUrl) {
    const iconSize = Math.round(Math.min(width, height) * 0.52);
    const IconComp = isVideo ? VideoFileIcon : isPdf ? PdfFileIcon : ImageFileIcon;
    return (
      <div
        ref={imgRef}
        style={{
          ...tileStyle,
          background: '#f5f5f6',
          border: '1px solid #e2e2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => { setError(false); setLoading(true); loadThumbnail(); }}
        title="Click to retry"
      >
        <IconComp size={iconSize} />
      </div>
    );
  }

  // ── Success: image + overlays ──
  return (
    <div ref={imgRef} className="thumb-tile" style={tileStyle}>
      <img
        src={thumbnailUrl}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',  // show full image — no cropping, letterbox gaps filled by tileStyle background
          display: 'block',
        }}
      />
      {isVideo && <PlayBadge />}
      {isPdf && !isVideo && <PdfBadge />}
    </div>
  );
};

// Call periodically to prevent unbounded memory growth
export const cleanupThumbnailCache = () => {
  if (globalThumbnailCache.size > 150) {
    // Keep the 75 most-recently-inserted entries (Map preserves insertion order)
    const keys = Array.from(globalThumbnailCache.keys());
    keys.slice(0, keys.length - 75).forEach(k => globalThumbnailCache.delete(k));
  }
};

export default CachedGridThumbnail;
